import React from 'react';
import { Table, Badge } from 'react-bootstrap';
import { useTable, useSortBy, UseSortByColumnProps, TableOptions } from 'react-table';
import { Case } from '../../types';
import { useNavigate, Link } from 'react-router-dom';

const CasesTable: React.FC<{ cases: Case[] }> = ({ cases = [] }) => {
  const navigate = useNavigate();

  const columns = React.useMemo(
    () => [
      {
        Header: 'Case Number',
        accessor: 'case_number',
        Cell: ({ row }: { row: { original: Case } }) => {
          const caseId = row.original.case_id;
          const caseNumber = row.original.case_number;
          return <Link to={`/case/${caseId}`}>{caseNumber}</Link>;
        },
      },
      { Header: 'Title', accessor: 'case_title' },
      { Header: 'Type', accessor: 'case_type' },
      { Header: 'Client', accessor: 'client' },
      { Header: 'Priority', accessor: 'priority' },
      { Header: 'Status', accessor: 'status' },
      { Header: 'Stage', accessor: (c: Case) => c.case_stage || '', id: 'case_stage' },
      {
        Header: 'Waiting On',
        accessor: (c: Case) => c.waiting_on || '',
        id: 'waiting_on',
        Cell: ({ row }: { row: { original: Case } }) => {
          const { waiting_on, waiting_since } = row.original;
          if (!waiting_on) return '-';
          const days = waiting_since
            ? Math.max(0, Math.floor((Date.now() - new Date(`${waiting_since}T00:00:00`).getTime()) / 86_400_000))
            : null;
          return (
            <span>
              <Badge bg="warning" text="dark">{waiting_on}</Badge>
              {days !== null && <span className="small text-muted ms-1">{days}d</span>}
            </span>
          );
        },
      },
      { Header: 'Start Date', accessor: 'start_date' },
      { 
        Header: 'Created Date', 
        accessor: 'created_date',
        Cell: ({ value }: { value?: string }) => {
          if (!value) return '-';
          try {
            const date = new Date(value);
            return date.toLocaleDateString();
          } catch {
            return value;
          }
        }
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: Array.isArray(cases) ? cases : [],
      disableSortRemove: true,
      disableMultiSort: true,
      autoResetSortBy: false,
      initialState: {
        sortBy: [
          {
            id: 'created_date',
            desc: true
          }
        ]
      }
    } as TableOptions<Case>,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  return (
    <Table {...getTableProps()} striped bordered hover>
      <thead>
        {headerGroups.map(headerGroup => {
          const { key: groupKey, ...groupProps } = headerGroup.getHeaderGroupProps();
          return (
            <tr key={groupKey} {...groupProps}>
              {headerGroup.headers.map(column => {
                const headerProps = column.getHeaderProps((column as unknown as UseSortByColumnProps<Case>).getSortByToggleProps());
                const { key, ...otherHeaderProps } = headerProps;
                return (
                  <th key={key} {...otherHeaderProps}>
                    {column.render('Header')}
                    <span>
                      {(column as unknown as UseSortByColumnProps<Case>).isSorted
                        ? (column as unknown as UseSortByColumnProps<Case>).isSortedDesc
                          ? ' ▽'
                          : ' △'
                        : ''}
                    </span>
                  </th>
                );
              })}
            </tr>
          );
        })}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);
          const { key: rowKey, ...rowProps } = row.getRowProps();
          return (
            <tr 
              key={rowKey} 
              {...rowProps}
              onClick={() => navigate(`/case/${row.original.case_id}`)}
              style={{ cursor: 'pointer' }}
            >
              {row.cells.map(cell => {
                const { key: cellKey, ...cellProps } = cell.getCellProps();
                return (
                  <td key={cellKey} {...cellProps}>
                    {cell.render('Cell')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default CasesTable;